import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { Conversation, Invoice, JSONApiDataObject, ReduxEntity } from 'Douleutaras/types';
import { makeRequest } from 'Douleutaras/services/apiClient';
import normalize from 'json-api-normalizer';
import moment from 'moment';

const INVOICES_ENDPOINT = 'api/public/v3/invoice/';
export const MAX_DUE_DAYS = 22; // Today doesn't count, so its actually 23.
export const MAX_OVERDUE_DAYS = 27; // Today doesn't count, so its actually 27

export const invoicesAdapter = createEntityAdapter<JSONApiDataObject<Invoice>>();
export const fetchInvoices = createAsyncThunk(
  'invoices/get',
  async ({ additionalGetParams, endpointIdentifier }: { additionalGetParams: {}, endpointIdentifier: string }, api) => {
    const response = await makeRequest(INVOICES_ENDPOINT,
      {
        method: 'GET',
        additionalGetParams
      })(api);

    return normalize(response.data, { endpoint: endpointIdentifier });
  }
);

export const lockInvoice = createAsyncThunk(
  'invoice/lock',
  async ({ invoiceId }: { invoiceId: number }, api) => {
    const response = await makeRequest(`${INVOICES_ENDPOINT}${invoiceId}/lock`,
      {
        method: 'GET'
      })(api);
  }
);

export const invoicesSlice = createSlice({
  name: 'invoices',
  initialState: invoicesAdapter.getInitialState({} as ReduxEntity<Invoice>),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchInvoices.fulfilled, (state, action) => {
      invoicesAdapter.upsertMany(state, action.payload.invoice || {});
      state.meta = action.payload.meta;
    });
  }
});

export interface UnpaidInvoicesDerived {
  pending: Array<JSONApiDataObject<Invoice>>;
  due: Array<JSONApiDataObject<Invoice>>;
  overDue: Array<JSONApiDataObject<Invoice>>;
}
export const selectUnpaidInvoices = createSelector([
  state => state.invoices,
  state => state.invoices?.meta?.['unpaid']
], (allInvoices, unpaidInvoices): UnpaidInvoicesDerived => {
  const unpaidInvoicesIds = unpaidInvoices?.data
    // Conditions that invoices need to meet in order to be considered as unpaid
    .map(unpaidInvoice => unpaidInvoice.id);
  const combinedInvoices: UnpaidInvoicesDerived = {
    pending: [],
    due: [],
    overDue: []
  };
  unpaidInvoicesIds?.forEach(unpaidInvoicesId => {
    const invoice = allInvoices.entities[unpaidInvoicesId];
    const createdDate = moment(invoice.attributes.created)
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0});
    const currentDate = moment();
    const daysDiff = currentDate.diff(createdDate, 'days');

    // Distributes invoices due / overdue and pending
    switch (true) {
      case daysDiff >= MAX_OVERDUE_DAYS:
        return combinedInvoices['overDue'].push(invoice);
      case daysDiff >= MAX_DUE_DAYS && daysDiff < MAX_OVERDUE_DAYS:
        return combinedInvoices['due'].push(invoice);
      default:
        return combinedInvoices['pending'].push(invoice);
    }
  });

  return combinedInvoices;
});

export const invoiceSelectors = invoicesAdapter.getSelectors();
export default invoiceSelectors;
